import {Component, DestroyRef, ElementRef, HostListener, inject, OnInit, viewChild, ChangeDetectionStrategy} from "@angular/core"
import * as paper from "paper"
import {ConnectionId} from "@socket-editor/types"
import {SocketConnection, SocketConnectionsService} from "@app/socket-editor/services/socket-connections.service"
import {takeUntilDestroyed} from "@angular/core/rxjs-interop"
import {NodeEditorTransformService} from "@node-editor/services/node-editor-transform.service"
import {Point} from "@app/node-editor/types/navigation"

/**
 * Draws connection lines between nodes/sockets
 */
@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-node-editor-canvas",
    templateUrl: "./node-editor-canvas.component.html",
    styleUrls: ["./node-editor-canvas.component.scss"],
})
export class NodeEditorCanvasComponent implements OnInit {
    readonly $visualCanvas = viewChild.required<ElementRef<HTMLCanvasElement>>("visualCanvas")

    private project!: paper.Project | undefined
    private connectionPaths = new Map<ConnectionId, paper.Path>()

    private destroyRef = inject(DestroyRef)
    protected element = inject(ElementRef)
    private connections = inject(SocketConnectionsService)
    private transformService = inject(NodeEditorTransformService)

    @HostListener("window:resize", ["$event"]) onResize(_event: Event) {
        this.updateVisualCanvasSize(this.element.nativeElement.getBoundingClientRect())
    }

    private resizeObserver?: ResizeObserver

    ngOnInit(): void {
        this.project = new paper.Project(this.$visualCanvas().nativeElement)

        this.resizeObserver = new ResizeObserver((_entries) => {
            this.updateVisualCanvasSize(this.element.nativeElement.getBoundingClientRect())
        })
        this.resizeObserver.observe(this.element.nativeElement)

        this.connections.onAdd$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((connections) => {
            connections.forEach((connection) => this.updateConnectionLine(connection))
        })
        this.connections.onUpdate$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((connections) => {
            connections.forEach((connection) => this.updateConnectionLine(connection))
        })
        this.connections.onDelete$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((connections) => {
            connections.forEach((connection) => this.deleteConnectionLine(connection.id))
        })

        this.transformService.transform?.transformChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((_transform) => {
            // this.redrawAll()
            this.connections.updateAll()
        })
    }

    ngOnDestroy() {
        this.resizeObserver?.unobserve(this.element.nativeElement)
    }

    updateConnectionLine(connection: SocketConnection): void {
        if (!this.project) {
            return
        }
        const existingConnection = this.connectionPaths.get(connection.id)
        const sourcePosition = this.transformService.logicalToEditorCoordinates(connection.source.position)
        const destinationPosition = this.transformService.logicalToEditorCoordinates(connection.destination.position)
        if (!sourcePosition || !destinationPosition) {
            // positions might not be available yet - we have to wait for nodes and sockets to be created
            return
        }
        if (existingConnection) {
            existingConnection.firstSegment.point.x = sourcePosition.x
            existingConnection.firstSegment.point.y = sourcePosition.y
            existingConnection.firstSegment.handleOut = new paper.Point(100 * this.transformService.scale, 0)
            existingConnection.lastSegment.point.x = destinationPosition.x
            existingConnection.lastSegment.point.y = destinationPosition.y
            existingConnection.lastSegment.handleIn = new paper.Point(-100 * this.transformService.scale, 0)
            existingConnection.strokeWidth = this.transformService.scale * 2
        } else {
            const path = new paper.Path({insert: false})
            this.project.activeLayer.addChild(path)
            path.strokeColor = this.strokeColor(connection, sourcePosition, destinationPosition)
            path.add(new paper.Point(sourcePosition))
            path.add(new paper.Point(destinationPosition))
            path.strokeWidth = this.transformService.scale * 2
            path.firstSegment.handleOut = new paper.Point(100 * this.transformService.scale, 0)
            path.lastSegment.handleIn = new paper.Point(-100 * this.transformService.scale, 0)
            this.connectionPaths.set(connection.id, path)
        }
    }

    strokeColor(connection: SocketConnection, sourcePosition: Point, destinationPosition: Point): paper.Color {
        if (connection.source.color) {
            if (connection.destination.color) {
                return new paper.Color({
                    gradient: {
                        stops: [connection.source.color, connection.destination.color],
                    },
                    origin: sourcePosition,
                    destination: destinationPosition,
                })
            } else {
                return new paper.Color(connection.source.color)
            }
        } else if (connection.destination.color) {
            return new paper.Color(connection.destination.color)
        } else {
            return new paper.Color("#b5b5b5")
        }
    }

    deleteConnectionLine(id: ConnectionId): void {
        this.connectionPaths.get(id)?.remove()
        this.connectionPaths.delete(id)
    }

    updateVisualCanvasSize(hostContainerBoundingRect: DOMRect): void {
        if (!this.project) return
        this.project.view.viewSize.width = hostContainerBoundingRect.width
        this.project.view.viewSize.height = hostContainerBoundingRect.height
    }
}
